import { useState, useEffect } from 'react';
import { fetchOpenPositions } from '../api/apiService';

/**
 * @typedef {Object} Position
 * @property {string} id
 * @property {string} account_id
 * @property {string} symbol_id
 * @property {string} symbol_name
 * @property {'buy'|'sell'} side
 * @property {number} qty
 * @property {string} entry_price
 * @property {string|null} current_price
 * @property {number} unrealized_pl
 * @property {number} market_value
 * @property {number} margin_used
 * @property {string|null} stop_loss
 * @property {string|null} take_profit
 * @property {'open'} status
 */

// The positions endpoint takes a UUID accountId and returns HTTP 400 for
// anything else. Validate locally so we never fire a request that is
// guaranteed to fail.
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isValidUuid = (value) =>
  typeof value === 'string' && UUID_REGEX.test(value);

/**
 * Fetches open positions for every provided trading account and aggregates
 * them into a single list plus a total count.
 *
 * The endpoint is per-account, so one request is issued per valid UUID id and
 * the results are combined. Requests that fail (an account with no portal
 * data, a transient error, etc.) are skipped so a single bad account never
 * blanks out the whole dashboard.
 *
 * @param {string|undefined|null} accessToken Portal access token.
 * @param {Array<string>} [accountIds=[]] Trading account ids to query.
 * @returns {{ positions: Position[], totalCount: number, loading: boolean, error: string|null }}
 */
const useOpenPositions = (accessToken, accountIds = []) => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stable key so we only refetch when the set of account ids actually changes
  // (financialData can be replaced by WebSocket updates with identical ids).
  const key = JSON.stringify([...accountIds].sort());

  useEffect(() => {
    const ids = JSON.parse(key).filter(isValidUuid);

    if (!accessToken || ids.length === 0) {
      setPositions([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all(
      ids.map((id) =>
        fetchOpenPositions(accessToken, id)
          .then((res) => (res.error ? [] : res.data?.positions ?? []))
          .catch(() => [])
      )
    )
      .then((results) => {
        if (cancelled) return;
        setPositions(results.flat());
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPositions([]);
        setError('Unable to load positions.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, key]);

  return {
    positions,
    totalCount: positions.length,
    loading,
    error,
  };
};

export default useOpenPositions;
